// CommitRow — single row in the history list.
// Props: commit (CommitSummary), onClick(oid): void

import type { CommitSummary } from "../diff/useLog.js";
import { useTranslation } from "../i18n";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";

type Translate = ReturnType<typeof useTranslation>["t"];

/** Returns a human-readable relative time string from an epoch-ms timestamp. */
function relativeTime(timestampMs: number, t: Translate): string {
  const diffMs = Date.now() - timestampMs;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return t("row.secondsAgo", { count: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t("row.minutesAgo", { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("row.hoursAgo", { count: hr });
  const day = Math.floor(hr / 24);
  if (day < 30) return t("row.daysAgo", { count: day });
  const mo = Math.floor(day / 30);
  if (mo < 12) return t("row.monthsAgo", { count: mo });
  return t("row.yearsAgo", { count: Math.floor(mo / 12) });
}

/** Tailwind accent for a git name-status letter. */
function statusColor(status: string): string {
  const c = status[0];
  if (c === "A" || c === "C") return "text-emerald-300";
  if (c === "D") return "text-rose-300";
  if (c === "R") return "text-sky-300";
  return "text-amber-300";
}

const MAX_CHIPS = 6;

export interface CommitRowProps {
  readonly commit: CommitSummary;
  readonly onClick: (oid: string) => void;
}

export function CommitRow({ commit, onClick }: CommitRowProps) {
  const { t } = useTranslation("history");
  const menu = useContextMenu();
  const menuItems: ContextMenuItem[] = [
    { label: t("menu.openCommit"), onClick: () => onClick(commit.oid) },
    { label: t("menu.copySha"), onClick: () => void navigator.clipboard?.writeText(commit.oid) },
    { label: t("menu.copyMessage"), onClick: () => void navigator.clipboard?.writeText(commit.message) },
  ];
  const isMerge = (commit.parents?.length ?? 0) > 1;
  const shortOid = commit.oid.slice(0, 7);
  const firstLine = commit.message.split("\n")[0];
  const files = commit.files ?? [];
  const shownFiles = files.slice(0, MAX_CHIPS);
  const extraFiles = files.length - shownFiles.length;

  return (
    <li
      role="button"
      tabIndex={0}
      data-commit-row
      data-oid={commit.oid}
      onClick={() => onClick(commit.oid)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(commit.oid); }}
      onContextMenu={(e) => menu.open(e, menuItems)}
      className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-slate-800/40 focus-visible:bg-slate-800/40 focus-visible:outline-none"
    >
      <code className="mt-0.5 shrink-0 rounded bg-slate-800 px-1.5 py-0.5 font-mono text-xs text-slate-400">{shortOid}</code>
      {isMerge && (
        <span className="mt-0.5 shrink-0 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-300">
          {t("row.merge")}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <span className="block truncate text-sm text-slate-200">{firstLine}</span>
        {shownFiles.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-1" data-commit-files>
            {shownFiles.map((f) => (
              <code
                key={`${f.status}:${f.path}`}
                title={`${f.status}  ${f.path}`}
                className="rounded bg-slate-800/70 px-1.5 py-0.5 font-mono text-[10px] text-slate-400"
              >
                <span className={statusColor(f.status)}>{f.status[0]}</span>{" "}
                {f.path.split("/").at(-1)}
              </code>
            ))}
            {extraFiles > 0 && (
              <span className="text-[10px] text-slate-500">{t("row.moreFiles", { count: extraFiles })}</span>
            )}
          </div>
        )}
      </div>
      <span className="mt-0.5 shrink-0 truncate text-xs text-slate-500">{commit.author}</span>
      <time className="mt-0.5 shrink-0 text-xs text-slate-600" dateTime={new Date(commit.timestampMs).toISOString()}>
        {relativeTime(commit.timestampMs, t)}
      </time>
      {menu.node}
    </li>
  );
}
